import {
  Bookmark,
  BookmarkCheck,
  Briefcase,
  Clock,
  DollarSign,
  MapPin,
  Trash2,
} from 'lucide-react';
import React, { useCallback, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CompanyLogoShared from '../../../components/CompanyLogo';
import { supabase } from '../../../lib/supabase';
import { computeSalaryIntelligenceForJob } from '../../../services/salaryIntelligenceService';

import { useAnonymizedJob } from '../hooks/useAnonymizedJob';
import { useUserProfile } from '../hooks/useUserProfile';
import { useJobContentTranslation } from '../hooks/useJobContentTranslation';
import { useApplyClickLogger } from '../hooks/useApplyClickLogger';
import { cleanSkills, freshnessLabel, localizeExperienceLevel, localizeJobType } from './Jobs/helpers';
import PeerlistIcon from './Jobs/PeerlistIcon';
import TakedownDialog from './Jobs/TakedownDialog';

// ---------------------------------------------------------------------------
// Types (exported so Jobs.tsx and future consumers can share them)
// ---------------------------------------------------------------------------

export interface Job {
  id: string;
  source: 'peerlist' | 'direct';
  /** 'linkedin' | 'naukri' | etc — for source badge when source === 'direct' */
  source_channel?: string | null;
  title: string;
  company: string;
  company_logo_url?: string;
  location: string;
  url: string;
  created: string;                  // ISO datetime
  description?: string;
  skills?: string[];
  salary_min?: number;
  salary_max?: number;
  salary_currency?: string;
  job_type?: string;
  experience_level?: string;
  remote?: boolean;
  external_job_id?: string;
  // --- Rich fields (Peerlist) -----------------------------------------------
  must_have_skills?: string[];
  good_to_have_skills?: string[];
  duration?: string;
  compensation_text?: string;
  posted_at?: string;
  applicant_count?: number;         // total applicants since posted (optional, sourced from job feed)
  // --- Ranking — set by rankJobs(), absent for signed-out users ---------------
  matchScore?:    number;     // 0-100 normalized
  matchedSkills?: string[];   // skills user already has
  missingSkills?: string[];   // skills user is missing
}

export interface JobCardProps {
  job: Job;
  isSaved: boolean;
  onToggleSave: (job: Job) => void;
  onApply: (job: Job) => void;
  onView: (job: Job) => void;
  /** Card-body click — opens the Job Detail drawer in Jobs.tsx. */
  onCardClick?: (job: Job) => void;
  /** Opens "Why did I get this match?" explainer for the match score ring. */
  onExplainMatch?: (job: Job) => void;
  /** Optional workflow status (Optimized / Applied) from agent history. */
  statusBadge?: React.ReactNode;
  /**
   * When true, render an owner-only "Take down" action.  Parent must
   * supply onTakedown — called with the external_slug after the RPC
   * succeeds so the parent can optimistically remove the row.
   */
  isOwner?: boolean;
  onTakedown?: (slug: string) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_SKILL_TAGS = 3;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatSalary(value: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
      notation: 'compact',
    }).format(value);
  } catch {
    return `${currency} ${value.toLocaleString()}`;
  }
}

function buildSalaryLabel(
  min: number | undefined,
  max: number | undefined,
  currency: string,
  t: (key: string, defaultValueOrOptions?: string | Record<string, unknown>, options?: Record<string, unknown>) => string,
): string {
  if (min != null && max != null) {
    return `${formatSalary(min, currency)} – ${formatSalary(max, currency)}`;
  }
  if (min != null) {
    return t('dashboard.jobs.jobCard.salaryFrom', { defaultValue: 'From {{amount}}', amount: formatSalary(min, currency) });
  }
  if (max != null) {
    return t('dashboard.jobs.jobCard.salaryUpTo', { defaultValue: 'Up to {{amount}}', amount: formatSalary(max, currency) });
  }
  return '';
}

// ---------------------------------------------------------------------------
// CompanyLogo sub-component
// ---------------------------------------------------------------------------

interface CompanyLogoProps {
  url?: string;
  company: string;
  logoAlt: string;
}

const CompanyLogo: React.FC<CompanyLogoProps> = ({ url, company, logoAlt }) => (
  <CompanyLogoShared
    url={url}
    company={company}
    imgClassName="w-9 h-9 rounded-[10px] object-contain shrink-0 p-0.5"
    imgStyle={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
    fallbackClassName="ds-icon-square t-card-title t-strong shrink-0"
    alt={logoAlt}
  />
);

// ---------------------------------------------------------------------------
// Match % ring — gradient arc + glass center (reference Jobs card UI)
// ---------------------------------------------------------------------------

const MATCH_RING_SIZE = 68;
const MATCH_RING_R = 24;
const MATCH_RING_COMPACT_SIZE = 52;
const MATCH_RING_COMPACT_R = 20;

export const JobMatchRing: React.FC<{
  score: number;
  gradientId: string;
  /** Smaller ring for canvas toolbar — value only, no "MATCH %" label */
  compact?: boolean;
  onClick?: () => void;
}> = ({ score, gradientId, compact = false, onClick }) => {
  const { t, i18n } = useTranslation();
  const pct = Math.max(0, Math.min(100, Math.round(score)));
  const size = compact ? MATCH_RING_COMPACT_SIZE : MATCH_RING_SIZE;
  const r = compact ? MATCH_RING_COMPACT_R : MATCH_RING_R;
  const cx = size / 2;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  const strokeW = compact ? 3.5 : 3;

  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper
      type={onClick ? 'button' : undefined}
      className={`ds-job-match-ring shrink-0${compact ? ' ds-job-match-ring--compact' : ''}${onClick ? ' ds-job-match-ring--clickable' : ''}`}
      role={onClick ? undefined : 'img'}
      aria-label={onClick
        ? t('dashboard.matchExplain.clickWhy', { score: pct, defaultValue: `Match: ${pct}% — click to see why` })
        : t('dashboard.jobs.jobCard.matchPercent', { defaultValue: '{{pct}} percent match', pct })}
      onClick={onClick ? (e: React.MouseEvent) => { e.stopPropagation(); onClick(); } : undefined}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="block"
        aria-hidden
      >
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#22d3ee" />
            <stop offset="50%" stopColor="#6C47FF" />
            <stop offset="100%" stopColor="#7c3aed" />
          </linearGradient>
        </defs>
        <circle
          cx={cx}
          cy={cx}
          r={r}
          fill="none"
          stroke="rgba(148, 163, 184, 0.22)"
          strokeWidth={strokeW}
        />
        <circle
          cx={cx}
          cy={cx}
          r={r}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth={strokeW}
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cx})`}
        />
      </svg>
      <div className="ds-job-match-ring__center" aria-hidden>
        {!compact && <span className="ds-job-match-ring__label">{t('dashboard.jobs.jobCard.matchLabel', 'MATCH %')}</span>}
        <span className="ds-job-match-ring__value">{pct}%</span>
        {onClick && !compact && (
          <span className="ds-job-match-ring__why">{t('dashboard.matchExplain.whyShort', 'Why?')}</span>
        )}
      </div>
    </Wrapper>
  );
};

// ---------------------------------------------------------------------------
// JobCard
// ---------------------------------------------------------------------------

const JobCard: React.FC<JobCardProps> = ({
  job: rawJob,
  isSaved,
  onToggleSave,
  onApply,
  onView,
  onCardClick,
  onExplainMatch,
  statusBadge,
  isOwner = false,
  onTakedown,
}) => {
  const { t, i18n } = useTranslation();
  const { profile } = useUserProfile();
  const job = useAnonymizedJob(rawJob);
  const { title: displayTitle, description: displayDescription, isTranslating } =
    useJobContentTranslation(job);

  const [isSignedIn, setIsSignedIn] = useState(false);
  const [showTakedownDialog, setShowTakedownDialog] = useState(false);
  const logClick = useApplyClickLogger();
  const matchGradId = `job-match-grad-${useId().replace(/:/g, '')}-${rawJob.id.replace(/[^a-zA-Z0-9_-]/g, '')}`;

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsSignedIn(!!session);
    });
  }, []);

  const currency = job.salary_currency ?? 'INR';
  const salaryIntel = computeSalaryIntelligenceForJob(rawJob, profile);
  const salaryLabel =
    (salaryIntel.hasData ? salaryIntel.estimatedLabel : null) ||
    buildSalaryLabel(job.salary_min, job.salary_max, currency, t) ||
    (job.compensation_text ?? '');

  const mustClean = cleanSkills(job.must_have_skills);
  const flatClean = cleanSkills(job.skills);
  const cardSkillsSource = mustClean.length > 0 ? mustClean : flatClean;
  const hasSkills = cardSkillsSource.length > 0;
  const visibleSkills = cardSkillsSource.slice(0, MAX_SKILL_TAGS);
  const extraSkills = Math.max(0, cardSkillsSource.length - MAX_SKILL_TAGS);

  const showDescription = Boolean(displayDescription);

  const label = freshnessLabel(job.posted_at ?? job.created, t, i18n.language);
  const postedAtMs = job.posted_at ? new Date(job.posted_at).getTime() : NaN;
  const isNew =
    job.posted_at != null &&
    job.posted_at !== '' &&
    Number.isFinite(postedAtMs) &&
    Date.now() - postedAtMs < 48 * 60 * 60 * 1000 &&
    Date.now() - postedAtMs >= 0;

  const locationLine = [
    job.location,
    job.remote ? t('dashboard.jobs.jobCard.remoteFriendly', 'Remote-friendly') : '',
  ]
    .filter(Boolean)
    .join(' · ');

  const handleToggleSave = useCallback(() => {
    onToggleSave(job);
  }, [job, onToggleSave]);

  const handleApply = useCallback(async () => {
    const clickId = await logClick({ job, kind: 'optimize_and_apply' });
    sessionStorage.setItem(
      'optimize.prefilledJob',
      JSON.stringify({
        jobUrl:        rawJob.url,
        jobTitle:      rawJob.title,
        companyName:   rawJob.company,
        source:        rawJob.source,
        externalJobId: rawJob.external_job_id ?? null,
        clickId:       clickId ?? undefined,
      }),
    );
    onApply(rawJob);
  }, [rawJob, job, logClick, onApply]);

  const handleView = useCallback(async () => {
    const clickId = await logClick({ job, kind: 'view' });
    sessionStorage.setItem(
      'pendingApplyConfirm',
      JSON.stringify({ clickId, job: rawJob, ts: Date.now() }),
    );
    onView(rawJob);
    window.open(rawJob.url, '_blank', 'noopener,noreferrer');
  }, [rawJob, job, logClick, onView]);

  const handleTakedownClick = useCallback(() => {
    if (!isOwner || !onTakedown || !job.external_job_id) return;
    setShowTakedownDialog(true);
  }, [isOwner, onTakedown, job.external_job_id]);

  const handleTakedownConfirm = useCallback(async () => {
    const slug = job.external_job_id;
    if (!slug || !onTakedown) return;
    const { error } = await supabase.rpc('takedown_peerlist_job', { p_slug: slug });
    if (error) throw new Error(error.message);
    setShowTakedownDialog(false);
    onTakedown(slug);
  }, [job.external_job_id, onTakedown]);

  const handleCardClick = useCallback(() => {
    onCardClick?.(rawJob);
  }, [rawJob, onCardClick]);

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  const scoreVal = Math.round(job.matchScore ?? 0);
  const showMatchRing = typeof job.matchScore === 'number' && scoreVal >= 1;

  const showMetaRow =
    Boolean(job.job_type || job.experience_level || job.remote || locationLine || salaryLabel);

  // Source badges retain brand colors (LinkedIn / Naukri / Peerlist identity)
  const srcBadge = job.source_channel === 'linkedin'
    ? <span className="ds-chip t-tag" style={{ background: 'rgba(10,102,194,0.10)', color: '#0A66C2', borderColor: 'rgba(10,102,194,0.25)' }}>
        <svg viewBox="0 0 24 24" width="8" height="8" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
        LinkedIn
      </span>
    : job.source_channel === 'naukri'
      ? <span className="ds-chip t-tag" style={{ background: 'rgba(217,119,6,0.10)', color: '#D97706', borderColor: 'rgba(217,119,6,0.25)' }}>Naukri</span>
      : job.source === 'peerlist'
        ? <span className="ds-chip t-tag" style={{ background: 'rgba(0,170,69,0.10)', color: '#00AA45', borderColor: 'rgba(0,170,69,0.25)' }}><PeerlistIcon size={8} />Peerlist</span>
        : job.source_channel
          ? <span className="ds-chip t-tag">{job.source_channel}</span>
          : null;

  return (
    <article
      role={onCardClick ? 'button' : undefined}
      tabIndex={onCardClick ? 0 : undefined}
      onClick={onCardClick ? handleCardClick : undefined}
      onKeyDown={onCardClick ? (e) => { if (e.key === 'Enter') handleCardClick(); } : undefined}
      className={[
        'ds-job-feed-card group',
        scoreVal >= 75 ? 'ds-job-feed-card--strong-fit' : '',
        onCardClick ? 'cursor-pointer' : '',
      ].filter(Boolean).join(' ')}
    >
      {showMatchRing && (
        <div className="ds-job-feed-card__match-anchor ds-job-feed-card__match-anchor--corner hidden sm:block" onClick={stop} role="presentation">
          <JobMatchRing
            score={scoreVal}
            gradientId={matchGradId}
            onClick={onExplainMatch ? () => onExplainMatch(rawJob) : undefined}
          />
        </div>
      )}

      <div className={`mb-4 flex items-start gap-3 ${showMatchRing ? 'sm:pr-[4.75rem]' : ''}`}>
        <div className="shrink-0">
          <CompanyLogo
            url={job.company_logo_url}
            company={job.company}
            logoAlt={t('dashboard.jobs.jobCard.companyLogoAlt', { defaultValue: '{{company}} logo', company: job.company })}
          />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="ds-job-feed-card__title t-h3 t-strong line-clamp-2 leading-snug">{displayTitle}</h3>
          <p className="ds-job-feed-card__company t-body mt-0.5 truncate font-medium" style={{ color: 'var(--ds-text-muted)' }}>
            {job.company}
          </p>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {statusBadge}
            {srcBadge}
            {isNew && <span className="ds-chip ds-chip-ok t-tag shrink-0">{t('dashboard.jobs.jobCard.newBadge', 'NEW')}</span>}
          </div>
        </div>
        {showMatchRing && (
          <div className="ds-job-feed-card__match-anchor ds-job-feed-card__match-anchor--inline shrink-0 sm:hidden" onClick={stop} role="presentation">
            <JobMatchRing
            score={scoreVal}
            gradientId={matchGradId}
            onClick={onExplainMatch ? () => onExplainMatch(rawJob) : undefined}
          />
          </div>
        )}
      </div>

      {hasSkills && (
        <div className="mb-4 flex flex-wrap gap-2">
          {visibleSkills.map((skill) => (
            <span key={skill} className="ds-job-feed-card__skill" title={skill}>
              {skill}
            </span>
          ))}
          {extraSkills > 0 && (
            <span className="ds-job-feed-card__skill ds-job-feed-card__skill--more" title={t('dashboard.jobs.jobCard.moreSkills', { defaultValue: '{{count}} more skills', count: extraSkills })}>
              +{extraSkills}
            </span>
          )}
        </div>
      )}

      {showDescription && (
        <p className="t-body mb-4 line-clamp-2 text-sm leading-relaxed" style={{ color: 'var(--ds-text-muted)' }}>
          {displayDescription}
          {isTranslating && (
            <span className="t-tag t-muted block mt-1">
              {t('dashboard.jobs.jobDrawer.translatingContent', 'Translating job details…')}
            </span>
          )}
        </p>
      )}

      {showMetaRow && (
        <div className="mb-4 flex flex-wrap items-center gap-1.5">
          {job.job_type && (
            <span className="ds-job-meta-badge">
              <Briefcase className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
              {localizeJobType(job.job_type, t)}
            </span>
          )}
          {job.experience_level && (
            <span className="ds-job-meta-badge">{localizeExperienceLevel(job.experience_level, t)}</span>
          )}
          {job.remote && (
            <span className="ds-job-meta-badge">{t('dashboard.jobs.jobCard.remote', 'Remote')}</span>
          )}
          {locationLine && (
            <span className="ds-job-meta-badge max-w-[11rem]">
              <MapPin className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
              <span className="truncate">{locationLine}</span>
            </span>
          )}
          {salaryLabel && (
            <span className="ds-job-meta-badge ds-job-meta-badge--salary">
              <DollarSign className="h-3 w-3 shrink-0" aria-hidden />
              {salaryLabel}
            </span>
          )}
        </div>
      )}

      <div className="flex-1 min-h-[0.5rem]" />

      <div
        className="mt-2 flex items-center justify-between gap-2 border-t pt-4"
        style={{ borderColor: 'var(--ds-divider)' }}
        onClick={stop}
        role="presentation"
      >
        {label ? (
          <span className="t-meta flex shrink-0 items-center gap-1 text-xs">
            <Clock className="h-3.5 w-3.5 shrink-0" />
            {label}
          </span>
        ) : (
          <span className="t-meta text-xs" style={{ color: 'var(--ds-text-disabled)' }}>
            {job.source === 'peerlist' ? (
              <a href={job.url} target="_blank" rel="noopener noreferrer" className="t-link" onClick={stop}>
                Peerlist
              </a>
            ) : null}
          </span>
        )}

        <div className="ds-job-feed-card__actions">
          {isOwner && job.external_job_id && (
            <button
              type="button"
              onClick={handleTakedownClick}
              aria-label={t('dashboard.jobs.jobCard.takeDownAria', { defaultValue: 'Take down {{title}}', title: displayTitle })}
              className="ds-btn ds-btn-ghost ds-btn-sm"
              style={{ width: 32, height: 32, padding: 0, color: 'var(--ds-text-muted)' }}
              title={t('dashboard.jobs.jobCard.takeDown', 'Take down')}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}

          <button
            type="button"
            title={isSaved ? t('dashboard.jobs.jobCard.saved', 'Saved') : t('dashboard.jobs.jobCard.saveJob', 'Save job')}
            onClick={handleToggleSave}
            disabled={!isSignedIn}
            className="ds-btn ds-btn-ghost ds-btn-sm"
            style={{
              width: 32,
              height: 32,
              padding: 0,
              color: isSaved ? 'var(--ds-brand)' : 'var(--ds-text-disabled)',
            }}
          >
            {isSaved ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
          </button>

          <button
            type="button"
            onClick={handleView}
            aria-label={t('dashboard.jobs.jobCard.viewAria', { defaultValue: 'View {{title}}', title: displayTitle })}
            className="ds-btn ds-btn-primary ds-btn-sm ds-job-feed-card__view-btn"
          >
            {t('dashboard.jobs.jobCard.view', 'View →')}
          </button>
        </div>
      </div>

      <TakedownDialog
        isOpen={showTakedownDialog}
        jobTitle={displayTitle}
        company={job.company}
        onClose={() => setShowTakedownDialog(false)}
        onConfirm={handleTakedownConfirm}
      />
    </article>
  );
};

export default React.memo(JobCard);
